/**
 * PDP free-gift progress (display only).
 * Add/remove is owned by <cart-free-gift> in the cart drawer.
 */

class PdpFreeGiftMount extends HTMLElement {
  connectedCallback() {
    if (this._hydrate()) return;

    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          if (this.isConnected && !this._hydrate()) this.remove();
        },
        { once: true }
      );
      return;
    }

    this.remove();
  }

  _hydrate() {
    const template = document.getElementById('free-gift-pdp-ssr');
    if (!template) return false;

    const node = template.content.querySelector('pdp-free-gift');
    if (!node) {
      this.remove();
      return true;
    }

    this.replaceWith(node.cloneNode(true));
    return true;
  }
}

class PdpFreeGift extends HTMLElement {
  connectedCallback() {
    this._onCartChange = this._onCartChange.bind(this);
    this._onCartRefresh = this._onCartRefresh.bind(this);
    this._onCartError = this._onCartError.bind(this);
    this._lastCartChangeAt = 0;
    this._addFailed = false;

    document.addEventListener('cart:change', this._onCartChange);
    document.addEventListener('cart:refresh', this._onCartRefresh);
    document.addEventListener('cart:error', this._onCartError);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this._onCartChange);
    document.removeEventListener('cart:refresh', this._onCartRefresh);
    document.removeEventListener('cart:error', this._onCartError);
  }

  _onCartChange(event) {
    this._lastCartChangeAt = Date.now();
    if (event.detail?.cart) this._applyCart(event.detail.cart);
  }

  _onCartError(event) {
    if (event.detail?.baseEvent !== 'free-gift:reconcile') return;
    this._addFailed = true;
    this.classList.remove('is-unlocked');
  }

  async _onCartRefresh() {
    if (Date.now() - this._lastCartChangeAt < 100) return;

    const cart = await this._fetchCart();
    if (cart) this._applyCart(cart);
  }

  async _fetchCart() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      return response.ok ? response.json() : null;
    } catch (error) {
      console.warn('[pdp-free-gift]', error);
      return null;
    }
  }

  _countItems(cart, variantId) {
    let eligibleCount = 0;
    let giftQuantity = 0;

    for (const item of cart.items || []) {
      const isGift =
        item.id === variantId &&
        item.properties?._free_gift != null &&
        item.properties._free_gift !== '';

      if (isGift) {
        giftQuantity += item.quantity;
      } else if (!item.properties?._bundle_gift) {
        // Bundle offer gifts are free extras, not paid items toward this threshold
        eligibleCount += item.quantity;
      }
    }

    return { eligibleCount, giftQuantity };
  }

  _messageFor(remainingCount, unlocked) {
    if (unlocked) return this.dataset.unlockedText || '';

    const one = this.dataset.progressTextOne || '';
    const many = this.dataset.progressText || '';
    const template = remainingCount === 1 && one ? one : many;
    return template.replace(/\[count\]/g, String(remainingCount));
  }

  _applyCart(cart) {
    const variantId = parseInt(this.dataset.variantId, 10);
    const threshold = parseInt(this.dataset.threshold, 10);
    if (!variantId || Number.isNaN(threshold) || threshold < 1) return;

    const { eligibleCount, giftQuantity } = this._countItems(cart, variantId);

    if (giftQuantity > 0 || eligibleCount < threshold) {
      this._addFailed = false;
    }

    // Gift in cart wins; threshold alone is optimistic until reconcile succeeds
    const unlocked = giftQuantity > 0 || (eligibleCount >= threshold && !this._addFailed);
    const remainingCount = Math.max(threshold - eligibleCount, 0);
    const progressCount = Math.min(eligibleCount, threshold);
    const progressPercent = Math.round((progressCount * 100) / threshold);
    const unlockedText = this.dataset.unlockedText || '';

    const messageEl = this.querySelector('[data-free-gift-message]');
    if (messageEl && !unlocked) {
      messageEl.textContent = this._messageFor(remainingCount, unlocked);
    }

    const successMessageEl = this.querySelector('[data-free-gift-success-message]');
    if (successMessageEl && unlockedText) {
      successMessageEl.textContent = unlockedText;
    }

    this.querySelector('[data-free-gift-progress]')?.setAttribute('aria-valuenow', String(progressCount));

    const fillEl = this.querySelector('[data-free-gift-progress-fill]');
    if (fillEl) fillEl.style.width = `${progressPercent}%`;

    this.classList.toggle('is-unlocked', unlocked);
    this.dataset.eligibleCount = String(eligibleCount);
    this.dataset.giftQuantity = String(giftQuantity);
  }
}

if (!window.customElements.get('pdp-free-gift-mount')) {
  window.customElements.define('pdp-free-gift-mount', PdpFreeGiftMount);
}

if (!window.customElements.get('pdp-free-gift')) {
  window.customElements.define('pdp-free-gift', PdpFreeGift);
}
